import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ControlValueAccessor, FormControl} from '@angular/forms';
import {Observable, of, ReplaySubject, Subscription} from 'rxjs';
import {MatSelectChange} from '@angular/material/select';
import {PartnerService} from '../../services/partner.service';
import {Partner} from '../../interface/partner';
import {PartnerFacility} from '../../interface/partner-facility';
import {startWith, takeUntil} from 'rxjs/operators';
import {Ofwca} from "../../interface/ofwca";
import {OfwcaService} from "../../services/ofwca.service";

@Component({
  selector: 'app-partner-facility-select',
  templateUrl: './partner-facility-select.component.html',
  styleUrls: ['./partner-facility-select.component.scss']
})
export class PartnerFacilitySelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() label: string;
  @Input() returnKey = false;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() partnerId: Observable<string>;
  @Input() partnerIdString: string = null;
  @Input() ofwcaIdString: string = null;
  @Input() initializeWith = null;

  externalControl = true;
  disabled = false;
  partner?: Partner = null;
  facilities: Array<PartnerFacility> = [];
  value?: number = null;

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor(
    private partnerService: PartnerService,
    private ofwcaService: OfwcaService
  ) { }

  ngOnInit(): void {
    if (this.partnerId) {
      this.partnerId
        .pipe(
          takeUntil(this.destroyed$),
          startWith(this.initializeWith)
        )
        .subscribe((partnerId?: string) => {
          if (!partnerId) {
            return;
          }

          this.loadFacilities(partnerId);
        });
    }
    if (this.partnerIdString){
      this.loadFacilities(this.partnerIdString);
    }
    if (this.ofwcaIdString) {
      this.loadOfwcaFacility(this.ofwcaIdString);
    }
    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }
  }

  loadFacilities(partnerId: string): void {
    this.partnerService.get(partnerId)
      .subscribe((partner: Partner) => {
        this.partner = partner;
        this.facilities = partner.facilities;
        if (this.returnKey && this.control.value) {
          this.control.setValue(this.facilities.find(facility => facility.id === this.control.value.id));
        }
      });
  }

  loadOfwcaFacility(ofwcaId: string): void {
    this.ofwcaService.get(ofwcaId)
      .subscribe((ofwca: Ofwca) => {
        if (ofwca.facility) {
          this.facilities = [ofwca.facility];

          const valueToSet = this.returnKey ? ofwca.facility : ofwca.facility.id;
          this.control.setValue(valueToSet);
        } else {
          this.facilities = [];
          this.clearValue();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }
}

